/**
 * Block 4 (#1892) — the per-account mutes.
 *
 * ── THE SEMANTICS ARE NOT THE OBVIOUS ONES, SO THE UI SAYS THEM ─────────────
 * Muting an account CLEARS its alert state: the dedup row is deleted, and
 * while the mute stands the pass mails that account nothing and writes nothing
 * about it. Lifting the mute does not replay anything — but if the account is
 * still over the line, the next pass sends it a FRESH alert, because as far as
 * that customer knows nothing was ever announced.
 *
 * The reason this has to be on screen rather than in a runbook: the intuitive
 * reading of a mute is "stop bothering this customer, permanently", and an
 * operator holding that reading will be surprised twice — once when the muted
 * customer's incident history restarts, and once when lifting the mute
 * produces a mail. Both surprises land on a paying customer's inbox.
 */
import type { AccountBalanceAlertSuppression } from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../formatters.ts';
import { OpsUserRef } from '../identity/OpsUserRef.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  EmptyState,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';

interface Props {
  readonly suppressions: readonly AccountBalanceAlertSuppression[];
  readonly onRestore: (accountId: string) => void;
  readonly restoringAccountId: string | null;
}

export function BalanceAlertSuppressionsTable({
  suppressions,
  onRestore,
  restoringAccountId,
}: Props): JSX.Element {
  const t = useT();

  if (suppressions.length === 0) {
    return (
      <EmptyState
        title={t('balance_alerts.suppressions.empty')}
        hint={t('balance_alerts.suppressions.semantics')}
        testId="balance-alerts-suppressions-empty"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p
        className="m-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper-mid"
        data-testid="balance-alerts-suppressions-semantics"
      >
        {t('balance_alerts.suppressions.semantics')}
      </p>

      <Table testId="balance-alerts-suppressions-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('balance_alerts.column.account')}</Th>
            <Th>{t('balance_alerts.column.reason')}</Th>
            <Th>{t('balance_alerts.column.suppressed_at')}</Th>
            <Th>{t('balance_alerts.column.suppressed_by')}</Th>
            <Th>{t('balance_alerts.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {suppressions.map((row) => (
              <Tr
                key={row.account_id}
                interactive={false}
                data-testid={`balance-alerts-suppression-row-${row.account_id}`}
              >
                <Td emphasis>
                  <span className="flex flex-col">
                    {/* A mute outlives the account it names — there is no
                        foreign key, by design — so a missing address is a
                        normal row, not a broken one. */}
                    <span>{row.account_email ?? t('balance_alerts.suppressions.no_email')}</span>
                    <span className="font-mono text-micro text-paper-low">{row.account_id}</span>
                  </span>
                </Td>
                <Td>{row.reason ?? '—'}</Td>
                <Td mono>{formatLocalTimestamp(row.created_at)}</Td>
                <Td mono>
                  <OpsUserRef id={row.created_by_ops_user_id} />
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={restoringAccountId === row.account_id}
                    onClick={() => onRestore(row.account_id)}
                    data-testid={`balance-alerts-restore-${row.account_id}`}
                  >
                    {t('balance_alerts.suppressions.restore')}
                  </Button>
                </Td>
              </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
